import {
	apiClient,
	Button,
	ButtonGroup,
	DropdownFormInput,
	parser,
} from "@ruter-as/web-components-and-tools";
import type React from "react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import widgetApi from "../../api/widgetApi";
import getChangeDates from "../../dateUtils";
import { useAuthenticatedAuthContext } from "../auth/Auth.component";

interface FormData {
	expirationDate: string;
}

const CancelTicketPage: React.FC = () => {
	const { id } = useParams();
	const startDates = getChangeDates();
	const { tickets, token, userData, refetchSubscriptions, selectedCompany } =
		useAuthenticatedAuthContext();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);

	const formMethods = useForm<FormData>({
		defaultValues: {
			expirationDate: startDates[0],
		},
	});

	const ticket = tickets.find((x) => x.id === id);

	if (!ticket) {
		throw new Error(`Could not find ticket with id=${id}`);
	}

	const isActiveTicket = ticket.status === "active";

	const cancelTicket = async (formData: FormData) => {
		setLoading(true);
		if (isActiveTicket) {
			const response = await apiClient.request(
				widgetApi.tickets.cancelActive(
					token,
					userData,
					ticket.id,
					parser.date.fromShortDateString(formData.expirationDate),
				),
			);
			if (response.error) {
				setLoading(() => {
					throw response.error;
				});
				return;
			}
		} else {
			const response = await apiClient.request(
				widgetApi.tickets.cancelPending(token, userData, ticket.id),
			);
			if (response.error) {
				setLoading(() => {
					throw response.error;
				});
				return;
			}
		}
		navigate("/abonnement");
		await refetchSubscriptions();
	};

	const product = selectedCompany ? "reiseabonnementet" : "billetten";

	return (
		<div data-test-id="cancel-ticket-page">
			<h1>{`Avslutt ${product}`}</h1>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(cancelTicket)}>
					{isActiveTicket && (
						<DropdownFormInput
							name="expirationDate"
							label="Billetten er gyldig til"
						>
							{startDates.map((date) => (
								<option key={date} value={date}>
									{date}
								</option>
							))}
						</DropdownFormInput>
					)}

					<p>Er du sikker på at du ønsker å kansellere {product}?</p>
					<ButtonGroup>
						<Button
							data-test-id="confirm-button"
							skin="danger"
							type="submit"
							loading={loading}
						>
							Ja
						</Button>
						<Button
							data-test-id="cancel-button"
							skin="cancel"
							onClick={() => {
								navigate("/abonnement");
							}}
							type="button"
						>
							Avbryt
						</Button>
					</ButtonGroup>
				</form>
			</FormProvider>
		</div>
	);
};

export default CancelTicketPage;
